/**
 * Scheduler API service for calendar and booking functionality
 */
import axios from 'axios';

const SCHEDULER_BASE_URL = process.env.REACT_APP_SCHEDULER_URL || 'http://localhost:8004';

// Create axios instance for scheduler service
const schedulerApi = axios.create({
  baseURL: SCHEDULER_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
schedulerApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Event Types API
export const eventTypesAPI = {
  list: async () => {
    const response = await schedulerApi.get('/api/event-types');
    return response.data;
  },

  get: async (eventTypeId) => {
    const response = await schedulerApi.get(`/api/event-types/${eventTypeId}`);
    return response.data;
  },

  create: async (eventTypeData) => {
    const response = await schedulerApi.post('/api/event-types', eventTypeData);
    return response.data;
  },

  update: async (eventTypeId, eventTypeData) => {
    const response = await schedulerApi.put(`/api/event-types/${eventTypeId}`, eventTypeData);
    return response.data;
  },

  delete: async (eventTypeId) => {
    await schedulerApi.delete(`/api/event-types/${eventTypeId}`);
  },
};

// Availability API
export const availabilityAPI = {
  list: async () => {
    const response = await schedulerApi.get('/api/availability');
    return response.data;
  },

  create: async (availabilityData) => {
    const response = await schedulerApi.post('/api/availability', availabilityData);
    return response.data;
  },

  update: async (availabilityId, availabilityData) => {
    const response = await schedulerApi.put(`/api/availability/${availabilityId}`, availabilityData);
    return response.data;
  },

  delete: async (availabilityId) => {
    await schedulerApi.delete(`/api/availability/${availabilityId}`);
  },
};

// Booking Pages API
export const bookingPagesAPI = {
  list: async () => {
    const response = await schedulerApi.get('/api/booking-pages');
    return response.data;
  },

  getBySlug: async (slug) => {
    // Public endpoint - no auth required
    const response = await axios.get(`${SCHEDULER_BASE_URL}/api/booking-pages/${slug}`);
    return response.data;
  },

  create: async (bookingPageData) => {
    const response = await schedulerApi.post('/api/booking-pages', bookingPageData);
    return response.data;
  },

  update: async (pageId, bookingPageData) => {
    const response = await schedulerApi.put(`/api/booking-pages/${pageId}`, bookingPageData);
    return response.data;
  },

  delete: async (pageId) => {
    await schedulerApi.delete(`/api/booking-pages/${pageId}`);
  },
};

// Bookings API
export const bookingsAPI = {
  list: async (statusFilter = null) => {
    const params = statusFilter ? { status_filter: statusFilter } : {};
    const response = await schedulerApi.get('/api/bookings', { params });
    return response.data;
  },

  get: async (bookingId) => {
    const response = await schedulerApi.get(`/api/bookings/${bookingId}`);
    return response.data;
  },

  create: async (bookingData) => {
    // Public endpoint - no auth required
    const response = await axios.post(`${SCHEDULER_BASE_URL}/api/bookings`, bookingData);
    return response.data;
  },

  cancel: async (bookingId, cancellationReason = null) => {
    // Public endpoint - guests can cancel
    const response = await axios.put(`${SCHEDULER_BASE_URL}/api/bookings/${bookingId}/cancel`, {
      cancellation_reason: cancellationReason,
    });
    return response.data;
  },

  reschedule: async (bookingId, newStartTime) => {
    // Public endpoint
    const response = await axios.put(`${SCHEDULER_BASE_URL}/api/bookings/${bookingId}/reschedule`, {
      start_time: newStartTime,
    });
    return response.data;
  },
};

// Available Slots API
export const slotsAPI = {
  getAvailableSlots: async (bookingPageId, date, timezone = 'UTC') => {
    // Public endpoint - no auth required
    const response = await axios.get(`${SCHEDULER_BASE_URL}/api/slots`, {
      params: {
        booking_page_id: bookingPageId,
        date: date,
        timezone: timezone,
      },
    });
    return response.data;
  },
};

// Calendar Connections API
export const calendarConnectionsAPI = {
  list: async () => {
    const response = await schedulerApi.get('/api/calendar-connections');
    return response.data;
  },

  create: async (connectionData) => {
    const response = await schedulerApi.post('/api/calendar-connections', connectionData);
    return response.data;
  },

  delete: async (connectionId) => {
    await schedulerApi.delete(`/api/calendar-connections/${connectionId}`);
  },

  sync: async (connectionId) => {
    const response = await schedulerApi.post(`/api/calendar-connections/${connectionId}/sync`);
    return response.data;
  },

  // Google OAuth
  getGoogleAuthUrl: async () => {
    const response = await schedulerApi.get('/api/calendar-connections/google/auth-url');
    return response.data;
  },

  connectGoogleCalendar: async (code, state) => {
    const response = await schedulerApi.post('/api/calendar-connections/google/callback', null, {
      params: { code, state }
    });
    return response.data;
  },

  disconnectGoogleCalendar: async () => {
    const response = await schedulerApi.post('/api/calendar-connections/google/disconnect');
    return response.data;
  },
};

// User Settings API
export const userSettingsAPI = {
  get: async (userId) => {
    const response = await schedulerApi.get(`/api/settings/users/${userId}`);
    return response.data;
  },

  create: async (settingsData) => {
    const response = await schedulerApi.post('/api/settings/users', settingsData);
    return response.data;
  },

  update: async (userId, settingsData) => {
    const response = await schedulerApi.put(`/api/settings/users/${userId}`, settingsData);
    return response.data;
  },

  delete: async (userId) => {
    await schedulerApi.delete(`/api/settings/users/${userId}`);
  },
};

// Events API (Event Pages)
export const eventsAPI = {
  list: async () => {
    const response = await schedulerApi.get('/api/events');
    return response.data;
  },

  get: async (eventId) => {
    const response = await schedulerApi.get(`/api/events/${eventId}`);
    return response.data;
  },

  getBySlug: async (slug) => {
    const response = await schedulerApi.get(`/api/events/public/${slug}`);
    return response.data;
  },

  create: async (eventData) => {
    const response = await schedulerApi.post('/api/events', eventData);
    return response.data;
  },

  update: async (eventId, eventData) => {
    const response = await schedulerApi.put(`/api/events/${eventId}`, eventData);
    return response.data;
  },

  delete: async (eventId) => {
    await schedulerApi.delete(`/api/events/${eventId}`);
  },

  publish: async (eventId) => {
    const response = await schedulerApi.post(`/api/events/${eventId}/publish`);
    return response.data;
  },
};

// Event Templates API
export const eventTemplatesAPI = {
  list: async () => {
    const response = await schedulerApi.get('/api/event-templates');
    return response.data;
  },
};

// Event Registrations API
export const eventRegistrationsAPI = {
  register: async (slug, registrationData) => {
    const response = await schedulerApi.post(`/api/events/${slug}/register`, registrationData);
    return response.data;
  },

  list: async (eventId) => {
    const response = await schedulerApi.get(`/api/events/${eventId}/registrations`);
    return response.data;
  },

  get: async (registrationId) => {
    const response = await schedulerApi.get(`/api/registrations/${registrationId}`);
    return response.data;
  },

  updateStatus: async (registrationId, status) => {
    const response = await schedulerApi.patch(`/api/registrations/${registrationId}/status`, { status });
    return response.data;
  },

  checkIn: async (registrationId) => {
    const response = await schedulerApi.post(`/api/registrations/${registrationId}/checkin`);
    return response.data;
  },
};

// Upload API (for event cover images)
export const uploadAPI = {
  generatePresignedUrl: async (filename, contentType) => {
    const response = await schedulerApi.post('/api/uploads/presigned-url', {
      filename,
      content_type: contentType,
    });
    return response.data;
  },

  uploadFile: async (file) => {
    // Get presigned URL
    const { upload_url, file_url } = await uploadAPI.generatePresignedUrl(
      file.name,
      file.type
    );

    // Upload directly to S3
    await axios.put(upload_url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });

    return file_url;
  },
};

export default schedulerApi;
